import React, { useMemo } from 'react';
import { AlertOctagon } from 'lucide-react';
import { useControllerFailingControls } from '../../../hooks/analytics/useControllerReports.ts';
import { WidgetCard } from './shared.tsx';

/**
 * Report #21 — Failing Controls Detail. Alert-list widget: one red-accent
 * card per control whose current test run concluded "ineffective".
 * `controlIdFilter`, when set, restricts rows to controls also present in
 * the Controller dashboard's slicer-filtered Test Summary (the persona's
 * primary dataset) — omitted for standalone (self-serve grid) usage.
 */
export const FailingControlsList: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerFailingControls();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  return (
    <WidgetCard
      title="Failing Controls"
      description="Controls whose latest test run concluded ineffective"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load failing controls.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No failing controls — all current test runs are effective."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <ul className="analytics-scroll space-y-3 max-h-96 overflow-y-auto">
        {rows.map((row) => (
          <li
            key={row.control_id}
            className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertOctagon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-gray-900 truncate">
                  {row.control_id} — {row.title}
                </p>
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 shrink-0">
                  {row.total_exceptions} exception{row.total_exceptions === 1 ? '' : 's'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {row.department ?? 'No department'} · {row.attributes_failed} attribute
                {row.attributes_failed === 1 ? '' : 's'} failed
                {row.confidence_score != null ? ` · ${Math.round(row.confidence_score * 100)}% confidence` : ''}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
};

export default FailingControlsList;
